<template>
	<div id="newClient">
		<JuniperPageHeader back="/juniper/clients" title="Creating new client" subtitle="Set up a new Juniper CMS client" />
		<div class="container">
			<div class="row justify-content-center align-items-center">
				<div class="col-md-9">
					<div class="card">
						<div class="card-body">
							<div class="d-flex flex-row justify-content-between align-items-center">
								<h1 class="mb-4 mt-1 text-primary">🎉 Creating new client</h1>
								<img width="100" src="../../../assets/images/new-client.svg" alt="" />
							</div>
							<!-- -->
							<div class="client-search">
								<ClientsSearch :selected="null" class="mb-2" v-if="client.mc_client_id === null" v-on:setClient="setClient" />
							</div>

							<!-- Client Name -->
							<div class="form-group">
								<label class="form-label">Client Name</label>
								<input type="text" class="form-control" v-model="client.name" placeholder="Client's name" />
								<small>Note: provide unique client name ( <i>example: Town of Juniper</i> ) </small>
							</div>

							<!-- Client Website URL -->
							<div class="form-group mb-3">
								<label class="form-label">Website URL</label>
								<input type="text" class="form-control" v-model="client.url" placeholder="Public URL" />
								<small>Note: this url is url for for main website not staging (<i>example: townofjuniper.com</i>) </small>
							</div>

							<!-- Staging URL is auto-generated by juniper-api after the client id is assigned (format: https://jp{id}stg.townweb.site). No input needed here. -->
							<div class="form-group mb-3">
								<small class="text-muted">Staging URL will be auto-generated (format: <code>jpNNNNstg.townweb.site</code>) once the client is created.</small>
							</div>

							<!-- Buttons -->
							<div class="d-flex justify-content-between align-items-center">
								<router-link class="btn btn-secondary" to="/juniper">Cancel</router-link>
								<button @click="createSubscription()" class="btn btn-primary" type="button" :disabled="loading">
									<template v-if="!loading">Create</template>
									<template v-else>
										<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
										Creating...
									</template>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import restApi from '../../../api/index'
import juniperApi from '@/api/juniperApi'
import ClientsSearch from '../../../components/clients/ClientsSearch.vue'

export default {
	name: 'NewClient',
	data() {
		return {
			loading: false,
			client: {
				name: '',
				url: '',
				mc_client_id: null,
			},
		}
	},
	components: { ClientsSearch },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		periodString: function() {
			return 'Last ' + this.period + ' days'
		},
	},
	created() {},
	methods: {
		// New provisioning flow:
		//   1. juniper-api POST /clients         → creates record, assigns id, generates staging_url
		//   2. mc2-api   POST /juniper/...       → provisions Plesk + Cloudflare using that staging_url
		//   3. juniper-api POST /clients/{id}/credentials → persist FTP creds
		// If step 2 fails the juniper record stays with status='preparing' for cleanup.
		async createSubscription() {
			this.loading = true

			let juniperClient
			try {
				const { data } = await juniperApi.post('/clients', {
					name: this.client.name,
					url: this.client.url,
					mc_client_id: this.client.mc_client_id,
				})
				juniperClient = data
			} catch (error) {
				this.loading = false
				this.$swal.fire({
					icon: 'error',
					title: this.extractError(error, 'Failed to create Juniper client record'),
					timer: 6000,
				})
				return
			}

			let pleskData
			try {
				const { data } = await restApi.post('/juniper/client/subscription', {
					name: juniperClient.name,
					url: juniperClient.url,
					staging_url: juniperClient.staging_url,
					mc_client_id: juniperClient.mc_client_id,
				})
				pleskData = data
			} catch (error) {
				this.loading = false
				this.$swal.fire({
					icon: 'error',
					title: this.extractError(error, 'Failed to provision Plesk/Cloudflare'),
					timer: 6000,
				})
				return
			}

			try {
				await juniperApi.post('/clients/' + juniperClient.id + '/credentials', {
					ftp_user: pleskData.ftp_user,
					ftp_password: pleskData.ftp_password,
				})
			} catch (error) {
				this.loading = false
				this.$swal.fire({
					icon: 'error',
					title: this.extractError(error, 'Plesk provisioned, but failed to save FTP credentials'),
					timer: 6000,
				})
				return
			}

			this.loading = false
			this.$swal.fire({
				icon: 'success',
				title: 'Client successfully created',
				timer: 4000,
			})
			this.$router.push('/juniper/clients/edit/' + juniperClient.id)
		},

		extractError(error, fallback) {
			const data = error && error.response && error.response.data
			if (!data) return fallback
			if (typeof data.error === 'string') return data.error
			if (data.error && typeof data.error === 'object') return Object.values(data.error).flat().join(' ')
			if (data.message) return data.message
			return fallback
		},

		setClient(client) {
			if (client) {
				this.client.name = client.name
				if (client.website) {
					this.client.url = client.website
						.replace('https://', '')
						.replace('http://', '')
						.replace('www.', '')
				}

				this.client.mc_client_id = client.id
			}
		},
	},

	watch: {
		'client.url'(newValue) {
			const clientUrl = newValue
				.replace('https://', '')
				.replace('http://', '')
				.replace('www.', '')
			if (clientUrl !== newValue) {
				this.client.url = clientUrl
			}
		},
	},
}
</script>
